import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircle,
  PaperPlaneTilt,
  Storefront,
  Sparkle,
  Buildings,
  Users,
  Receipt,
  MapPin,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import { PageShell } from "@/components/Layout";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#e2a9f1] focus:outline-none px-4 py-3 text-[#EDEDED] rounded-sm placeholder:text-[#444]";

const empty = {
  contact_name: "",
  venue_name: "",
  email: "",
  contact_number: "",
  venue_website: "",
  venue_type: "",
  message: "",
};

const BRING_TO_TABLE = [
  {
    icon: <Users size={20} weight="thin" />,
    title: "Respectable clientele",
    body: "Discerning guests with disposable income who are keen to spend in your venue.",
  },
  {
    icon: <Sparkle size={20} weight="thin" />,
    title: "Repeat business",
    body: "Many of our event guests may not have been to your venue before — make a good impression and turn them into repeat customers who prioritise you when they have free time or want to meet people privately.",
  },
  {
    icon: <Storefront size={20} weight="thin" />,
    title: "Enterprise-grade booking",
    body: "Digital QR-code tickets and simple check-in — we handle the booking flow from start to finish.",
  },
  {
    icon: <Receipt size={20} weight="thin" />,
    title: "ePOS systems & marketing",
    body: "Our sister business can also offer table booking software, enterprise-level websites, digital ticket sales, POS systems and payment processing at market-leading rates — whether you're a social venue or a play venue.",
  },
];

const SOCIAL_CRITERIA = [
  "Local venues with good public and road transport links",
  "Capacity for 60–200 people",
  "Hotels nearby are a bonus",
  "Exclusive use of the venue for approx. 3 hours (between 7pm & 11pm)",
  "Or exclusive use of a private area of the venue with its own bar (that the public cannot access)",
  "Good range of draught or bottled drinks, spirits, wines, bubbles, cocktails and no / low-alcohol alternatives",
  "Food is not served at our events",
];

const PLAY_CRITERIA = [
  "Local venues with good public and road transport links",
  "Hotels nearby essential",
  "Capacity for 50–400 people",
  "Exclusive use of the venue for a predetermined period",
  "Or exclusive use of a certain area of the venue for a predetermined period",
  "Good range of drinks to serve guests and increase your bar sales (including no / low-alcohol alternatives)",
  "A designated socialising area as well as plenty of areas for guests to let loose",
  "Potential for monthly / bi-monthly events to suit your schedule",
];

const Bullet = ({ children }) => (
  <li className="flex gap-3 text-[#EDEDED]/85 leading-relaxed">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#e2a9f1] shrink-0" />
    <span>{children}</span>
  </li>
);

export default function ForVenues() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/venue-inquiries", form);
      setDone(true);
      toast.success("Inquiry received");
    } catch (err) {
      const d = err.response?.data?.detail || "Could not send your inquiry";
      toast.error(typeof d === "string" ? d : "Could not send your inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* Hero */}
      <section className="px-6 lg:px-12 pt-16 pb-12" data-testid="for-venues-page">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="font-mono-overline text-[#e2a9f1] mb-4">For Venues</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif-display text-[#EDEDED] leading-[0.95] mb-6">
              Let&rsquo;s have
              <br />
              <span className="display-italic text-[#e2a9f1]">a conversation.</span>
            </h1>
            <p className="text-lg text-[#EDEDED]/80 leading-relaxed max-w-2xl">
              If you are a venue owner or operator looking for an increase in footfall or sales,
              then let&rsquo;s have a conversation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What we bring to the table */}
      <section className="px-6 lg:px-12 py-16 border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-overline text-[#e2a9f1] mb-4">What We Bring To The Table</div>
          <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] mb-12 max-w-2xl leading-tight">
            Four reasons to host a Swingle evening.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {BRING_TO_TABLE.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="bg-[#131313] border border-[#2A2A2A] p-8 hover:border-[#e2a9f1]/40 transition-colors"
                data-testid={`bring-${i}`}
              >
                <div className="text-[#e2a9f1] mb-4">{b.icon}</div>
                <h3 className="text-2xl font-serif-display text-[#EDEDED] mb-3 leading-tight">{b.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Venues */}
      <section className="px-6 lg:px-12 py-20 border-t border-[#2A2A2A]" data-testid="social-venues-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-overline text-[#e2a9f1] mb-4 flex items-center gap-2">
              <Buildings size={14} weight="thin" /> Social Venues
            </div>
            <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] leading-tight mb-6">
              Do you own a bar, private room or event space with a
              {" "}
              <span className="display-italic text-[#e2a9f1]">premises licence?</span>
            </h2>
            <p className="text-[#EDEDED]/80 leading-relaxed mb-5">
              We&rsquo;re always on the lookout for venues to partner with for our social events
              across the country.
            </p>
            <p className="text-[#EDEDED]/80 leading-relaxed mb-5">
              Our social events are an event where our guests can mingle and network with
              like-minded people in a no-pressure, no-play environment — and all of our guests
              are respectful of these rules.
            </p>
            <p className="text-[#EDEDED]/80 leading-relaxed">
              If you&rsquo;re looking to add an increase in sales and get your venue in the front
              of the minds of respectable clientele with disposable income then let&rsquo;s have
              a conversation.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#131313] border border-[#2A2A2A] p-8 md:p-10">
              <div className="font-mono-overline text-[#A3A3A3] mb-6">What We Look For in Social Venues</div>
              <ul className="space-y-3">
                {SOCIAL_CRITERIA.map((c) => (
                  <Bullet key={c}>{c}</Bullet>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Play Venues */}
      <section className="px-6 lg:px-12 py-20 border-t border-[#2A2A2A]" data-testid="play-venues-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-overline text-[#e2a9f1] mb-4 flex items-center gap-2">
              <MapPin size={14} weight="thin" /> Play Venues
            </div>
            <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] leading-tight mb-6">
              Do you own or operate a
              {" "}
              <span className="display-italic text-[#e2a9f1]">club or specialist venue?</span>
            </h2>
            <p className="text-[#EDEDED]/80 leading-relaxed mb-5">
              Are some weekends quieter than others? We can help with that. We&rsquo;re always on
              the lookout for venues to partner with to put on memorable and delectable nights of
              all kinds — full of potential repeat customers that will revisit your club.
            </p>
            <p className="text-[#EDEDED]/80 leading-relaxed">
              Want to have a discussion about Swingle hosting an event at your venue and putting
              your venue front of mind next time customers have time to let their hair down?
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#131313] border border-[#2A2A2A] p-8 md:p-10">
              <div className="font-mono-overline text-[#A3A3A3] mb-6">What We Look For in Play Venues</div>
              <ul className="space-y-3">
                {PLAY_CRITERIA.map((c) => (
                  <Bullet key={c}>{c}</Bullet>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Venue contact form */}
      <section className="px-6 lg:px-12 py-20 border-t border-[#2A2A2A]" data-testid="venue-form-section">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono-overline text-[#e2a9f1] mb-4">Start the conversation</div>
          <h2 className="text-4xl md:text-5xl font-serif-display text-[#EDEDED] leading-tight mb-10">
            Tell us about
            <br />
            <span className="display-italic text-[#e2a9f1]">your venue.</span>
          </h2>

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-[#131313] border border-[#e2a9f1]/40 p-10 text-center"
              data-testid="venue-form-success"
            >
              <CheckCircle size={42} weight="thin" className="text-[#e2a9f1] mx-auto mb-4" />
              <h3 className="text-3xl font-serif-display text-[#EDEDED] mb-3">Thank you.</h3>
              <p className="text-[#A3A3A3] leading-relaxed max-w-md mx-auto">
                Your venue inquiry is with us. The team will be in touch within a few working days.
              </p>
              <button
                type="button"
                onClick={() => { setForm(empty); setDone(false); }}
                data-testid="venue-form-send-another"
                className="mt-8 font-mono-overline text-[#e2a9f1] hover:text-[#efc8f7]"
              >
                Submit another venue →
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-[#131313] border border-[#2A2A2A] p-8 md:p-10 space-y-6"
              data-testid="venue-form"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                    Contact Name<span className="text-[#e2a9f1] ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.contact_name}
                    onChange={update("contact_name")}
                    placeholder="Your full name"
                    className={inputCls}
                    data-testid="venue-contact-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                    Venue Name<span className="text-[#e2a9f1] ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.venue_name}
                    onChange={update("venue_name")}
                    placeholder="Name of your venue"
                    className={inputCls}
                    data-testid="venue-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                    Email Address<span className="text-[#e2a9f1] ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@yourvenue.com"
                    className={inputCls}
                    data-testid="venue-email-input"
                  />
                </div>
                <div>
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                    Contact Number<span className="text-[#e2a9f1] ml-1">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.contact_number}
                    onChange={update("contact_number")}
                    placeholder="+44 …"
                    className={inputCls}
                    data-testid="venue-contact-number-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">Venue Website</label>
                  <input
                    type="url"
                    value={form.venue_website}
                    onChange={update("venue_website")}
                    placeholder="https://"
                    className={inputCls}
                    data-testid="venue-website-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-overline text-[#A3A3A3] block mb-3">Venue type</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { v: "social", l: "Social" },
                      { v: "play", l: "Play" },
                      { v: "both", l: "Both" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setForm({ ...form, venue_type: opt.v })}
                        data-testid={`venue-type-${opt.v}`}
                        className={`px-5 py-2 rounded-sm border uppercase tracking-[0.2em] text-xs font-medium transition-all ${
                          form.venue_type === opt.v
                            ? "bg-[#e2a9f1] text-[#0A0A0A] border-[#e2a9f1]"
                            : "bg-[#0A0A0A] text-[#A3A3A3] border-[#2A2A2A] hover:border-[#e2a9f1]/60"
                        }`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">A little more (optional)</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Capacity, location, anything else we should know"
                    className={inputCls + " resize-y"}
                    data-testid="venue-message-input"
                  />
                </div>
              </div>

              <div className="text-xs text-[#A3A3A3] leading-relaxed border-l-2 border-[#e2a9f1]/40 pl-4">
                Fields marked <span className="text-[#e2a9f1]">*</span> are required. We&rsquo;ll
                review your venue and get back to you within a few working days.
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="venue-form-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm py-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PaperPlaneTilt size={18} weight="thin" />
                {submitting ? "Sending…" : "Send Inquiry"}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}
